import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './ProductReviewsPage.css';

function ProductReviewsPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, fetchWithAuth } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [newRating, setNewRating] = useState({ stars: 0, comment: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyComment, setReplyComment] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editStars, setEditStars] = useState(0);
  const [editComment, setEditComment] = useState('');
  const [userHasRated, setUserHasRated] = useState(false);
  const [userRating, setUserRating] = useState(null);

  useEffect(() => {
    fetchData();
  }, [productId, user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productRes, ratingsRes] = await Promise.all([
        fetch(`/api/products/${productId}?lean=true`),
        fetch(`/api/ratings/product/${productId}`)
      ]);

      if (productRes.ok) {
        const productData = await productRes.json();
        setProduct(productData);
      }

      if (ratingsRes.ok) {
        const ratingsData = await ratingsRes.json();
        setRatings(ratingsData.threads || []);
        
        // Prüfe ob eingeloggter User bereits bewertet hat
        if (isAuthenticated && user) {
          const userReview = (ratingsData.threads || []).find(
            r => r.user.id === user.id && !r.parentRatingId
          );
          if (userReview) {
            setUserHasRated(true);
            setUserRating(userReview);
          } else {
            setUserHasRated(false);
            setUserRating(null);
          }
        }
      }
    } catch (err) {
      setError('Fehler beim Laden: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRating = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setError('Bitte melde dich an um zu bewerten');
      return;
    }
    if (newRating.stars === 0) {
      setError('Bitte wähle eine Sternbewertung');
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetType: 'product',
          targetId: productId,
          stars: newRating.stars,
          comment: newRating.comment
        })
      });

      if (response.ok) {
        setNewRating({ stars: 0, comment: '' });
        setError('');
        await fetchData();
      } else {
        const errorData = await response.json();
        if (response.status === 409) {
          setError('Du hast dieses Produkt bereits bewertet. Scrolle nach unten um deine Bewertung zu sehen.');
        } else {
          setError(errorData.message || 'Fehler beim Speichern der Bewertung');
        }
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditRating = async (ratingId) => {
    if (!editComment && !editStars) {
      setError('Bitte gib neue Inhalte ein');
      return;
    }

    try {
      const body = {};
      if (editComment) body.comment = editComment;
      if (editStars) body.stars = editStars;

      const response = await fetchWithAuth(`/api/ratings/${ratingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        setEditingId(null);
        setEditComment('');
        setEditStars(0);
        await fetchData();
      } else {
        setError('Fehler beim Bearbeiten');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRating = async (ratingId) => {
    if (!window.confirm('Wirklich löschen?')) return;

    try {
      const response = await fetchWithAuth(`/api/ratings/${ratingId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchData();
      } else {
        setError('Fehler beim Löschen');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCensorRating = async (ratingId, mode) => {
    if (!window.confirm(`Wirklich ${mode === 'full' ? 'vollständig löschen' : 'zensieren'}?`)) return;

    try {
      const response = await fetchWithAuth(`/api/ratings/${ratingId}/censor`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode })
      });

      if (response.ok) {
        await fetchData();
      } else {
        setError('Fehler beim Zensieren');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReply = async (parentId) => {
    if (!isAuthenticated) {
      setError('Bitte melde dich an um zu antworten');
      return;
    }
    if (!replyComment.trim()) {
      setError('Antwort erforderlich');
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/ratings/${parentId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: replyComment })
      });

      if (response.ok) {
        setReplyingTo(null);
        setReplyComment('');
        await fetchData();
      } else {
        setError('Fehler beim Speichern der Antwort');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="reviews-page"><div className="loading">⏳ Lädt...</div></div>;

  return (
    <div className="reviews-page">
      {product && (
        <div className="review-header">
          <button className="back-btn" onClick={() => navigate(-1)}>← Zurück</button>
          <div className="product-info">
            {product.imageUrl && <img src={product.imageUrl} alt={product.name} className="product-thumb" />}
            <div>
              <h1>{product.name}</h1>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-msg">{error}</div>}

      {/* Bewertungsformular */}
      {isAuthenticated && !userHasRated && (
        <div className="rating-form">
          <h3>🌟 Deine Bewertung</h3>
          <form onSubmit={handleSubmitRating}>
            <div className="stars-selector">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`star-btn ${newRating.stars >= s ? 'active' : ''}`}
                  onClick={() => setNewRating({ ...newRating, stars: s })}
                >
                  ★
                </button>
              ))}
            </div>
            <textarea
              placeholder="Kommentar (optional, max. 2000 Zeichen)"
              value={newRating.comment}
              onChange={(e) => setNewRating({ ...newRating, comment: e.target.value })}
              maxLength="2000"
            />
            <button type="submit" className="submit-btn">Abgeben</button>
          </form>
        </div>
      )}

      {isAuthenticated && userHasRated && userRating && (
        <div className="already-rated-notice">
          <div className="notice-header">
            <strong>✅ Du hast dieses Produkt bereits bewertet</strong>
          </div>
          <div className="user-rating-preview">
            <div className="preview-stars">{'★'.repeat(userRating.stars)}</div>
            {userRating.comment && (
              <div className="preview-comment">{userRating.comment}</div>
            )}
            <div className="preview-date">
              Bewertet am {new Date(userRating.createdAt).toLocaleDateString('de-DE')}
            </div>
          </div>
          <div className="notice-hint">
            💡 Scrolle nach unten um deine Bewertung zu bearbeiten oder zu löschen
          </div>
        </div>
      )}

      {!isAuthenticated && (
        <div className="login-prompt">
          📝 Bitte melde dich an um zu bewerten
        </div>
      )}

      {/* Bewertungslist */}
      <div className="ratings-list">
        <h3>💬 {ratings.length} Bewertungen</h3>
        {ratings.length === 0 ? (
          <div className="no-ratings">Noch keine Bewertungen</div>
        ) : (
          ratings.map((rating) => (
            <RatingItem
              key={rating.id}
              rating={rating}
              currentUser={user}
              handleDeleteRating={handleDeleteRating}
              handleCensorRating={handleCensorRating}
              handleEditRating={handleEditRating}
              handleReply={handleReply}
              editingId={editingId}
              setEditingId={setEditingId}
              editStars={editStars}
              setEditStars={setEditStars}
              editComment={editComment}
              setEditComment={setEditComment}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              replyComment={replyComment}
              setReplyComment={setReplyComment}
            />
          ))
        )}
      </div>
    </div>
  );
}

function RatingItem({
  rating,
  currentUser,
  handleDeleteRating,
  handleCensorRating,
  handleEditRating,
  handleReply,
  editingId,
  setEditingId,
  editStars,
  setEditStars,
  editComment,
  setEditComment,
  replyingTo,
  setReplyingTo,
  replyComment,
  setReplyComment
}) {
  const isOwner = currentUser && currentUser.id === rating.user.id;
  const isAdmin = currentUser && currentUser.roles && currentUser.roles.includes('admin');
  const isCensored = rating.status === 'censored';
  const isReply = !!rating.parentRatingId;
  const isEditing = editingId === rating.id;
  const isReplying = replyingTo === rating.id;

  return (
    <div className={`rating-item ${isCensored ? 'censored' : ''} ${isReply ? 'reply' : ''}`}>
      <div className="rating-header">
        <div className="user-meta">
          <div className="user-info">
            {rating.user.profileImage ? (
              <img src={rating.user.profileImage} alt={rating.user.username} className="user-avatar" />
            ) : (
              <div className="user-avatar-placeholder">👤</div>
            )}
            <strong className="username">{rating.user.username}</strong>
          </div>
          {!isCensored && rating.stars > 0 && (
            <span className="stars">{'★'.repeat(rating.stars)}</span>
          )}
          <span className="date">{new Date(rating.createdAt).toLocaleDateString('de-DE')}</span>
        </div>
        <div className="rating-actions">
          {isOwner && (
            <>
              <button 
                className="action-btn edit" 
                onClick={() => {
                  setEditingId(rating.id);
                  setEditStars(rating.stars);
                  setEditComment(rating.comment);
                }} 
                title="Bearbeiten"
              >
                ✏️
              </button>
              <button 
                className="action-btn delete" 
                onClick={() => handleDeleteRating(rating.id)} 
                title="Löschen"
              >
                🗑️
              </button>
            </>
          )}
          {(isAdmin || (currentUser && currentUser.roles && currentUser.roles.includes('moderator'))) && (
            <>
              <button 
                className="action-btn censor-partial" 
                onClick={() => handleCensorRating(rating.id, 'partial')} 
                title="Teilweise zensieren"
              >
                ⚠️
              </button>
              <button 
                className="action-btn censor-full" 
                onClick={() => handleCensorRating(rating.id, 'full')} 
                title="Vollständig löschen (mit AGB-Hinweis)"
              >
                🚫
              </button>
              {!isOwner && (
                <button 
                  className="action-btn delete" 
                  onClick={() => handleDeleteRating(rating.id)} 
                  title="Löschen"
                >
                  🗑️
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {isEditing && isOwner ? (
        <div className="edit-form">
          {!isReply && (
            <div className="stars-selector">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`star-btn ${editStars >= s ? 'active' : ''}`}
                  onClick={() => setEditStars(s)}
                >
                  ★
                </button>
              ))}
            </div>
          )}
          <textarea 
            value={editComment} 
            onChange={(e) => setEditComment(e.target.value)} 
            maxLength="2000" 
          />
          <div className="edit-actions">
            <button 
              className="save-btn" 
              onClick={() => handleEditRating(rating.id)}
            >
              ✓ Speichern
            </button>
            <button 
              className="cancel-btn" 
              onClick={() => {
                setEditingId(null);
                setEditComment('');
              }}
            >
              ✕ Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <div className="rating-content">
          <p>{rating.comment}</p>
        </div>
      )}

      {!isReply && (
        <>
          {isReplying ? (
            <div className="reply-form">
              <textarea 
                placeholder="Antwort..." 
                value={replyComment} 
                onChange={(e) => setReplyComment(e.target.value)} 
                maxLength="2000" 
              />
              <button 
                className="reply-submit" 
                onClick={() => handleReply(rating.id)}
              >
                Antworten
              </button>
            </div>
          ) : (
            <button 
              className="reply-btn" 
              onClick={() => setReplyingTo(rating.id)}
            >
              💬 Antworten
            </button>
          )}

          {rating.replies && rating.replies.length > 0 && (
            <div className="replies">
              {rating.replies.map((reply) => (
                <RatingItem
                  key={reply.id}
                  rating={reply}
                  currentUser={currentUser}
                  handleDeleteRating={handleDeleteRating}
                  handleCensorRating={handleCensorRating}
                  handleEditRating={handleEditRating}
                  handleReply={handleReply}
                  editingId={editingId}
                  setEditingId={setEditingId}
                  editStars={editStars}
                  setEditStars={setEditStars}
                  editComment={editComment}
                  setEditComment={setEditComment}
                  replyingTo={replyingTo}
                  setReplyingTo={setReplyingTo}
                  replyComment={replyComment}
                  setReplyComment={setReplyComment}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ProductReviewsPage;
